/**
 * Shared Safari orientation-settle timer.
 *
 * WebKit finishes chrome + env(safe-area-inset-*) AFTER orientationchange.
 * Callers register hooks that run once the settle window elapses (coalesced).
 * An optional preamble (safe-top floor) always runs before subscriber hooks
 * so remounted chrome reads the updated `--cvx-safe-top-floor`.
 */

const isBrowser = typeof window !== 'undefined';

/** Safari chrome + inset settle window after rotate (ms). */
export const ORIENTATION_SETTLE_MS = 400;

const hooks = new Set();
let preambleFn = null;
let settleTimerId = 0;
/** Date.now() deadline while an orientation settle is pending (0 = idle). */
let settleUntil = 0;

/**
 * Optional work that must run BEFORE remount hooks (safe-top floor).
 * @param {null | (() => void)} fn
 */
export function setOrientationSettlePreamble(fn) {
  preambleFn = typeof fn === 'function' ? fn : null;
}

/**
 * Register a callback to run when orientation settle completes (after preamble).
 * @param {() => void} fn
 * @returns {() => void} unsubscribe
 */
export function onOrientationSettled(fn) {
  hooks.add(fn);
  return () => {
    hooks.delete(fn);
  };
}

/**
 * True while we are waiting for Safari to finish orientation chrome/insets.
 */
export function isOrientationSettling(now = Date.now()) {
  return settleTimerId !== 0 || (settleUntil > 0 && now < settleUntil);
}

/**
 * Coalesce orientation settle: run preamble immediately (safe-top floor must
 * not sit on a stale orientation for 400ms), then wait ORIENTATION_SETTLE_MS
 * and run preamble again + remount hooks once Safari finishes chrome/insets.
 */
export function scheduleOrientationSettle() {
  if (!isBrowser) return;
  if (preambleFn) {
    try {
      preambleFn();
    } catch {
      /* ignore */
    }
  }
  settleUntil = Date.now() + ORIENTATION_SETTLE_MS;
  clearTimeout(settleTimerId);
  settleTimerId = setTimeout(() => {
    settleTimerId = 0;
    settleUntil = 0;
    if (preambleFn) {
      try {
        preambleFn();
      } catch {
        /* ignore */
      }
    }
    hooks.forEach((fn) => {
      try {
        fn();
      } catch {
        /* ignore hook errors */
      }
    });
  }, ORIENTATION_SETTLE_MS);
}

/** @internal test helper */
export function __resetOrientationSettleForTests() {
  clearTimeout(settleTimerId);
  settleTimerId = 0;
  settleUntil = 0;
  hooks.clear();
  preambleFn = null;
}
